import { Minus, Plus } from "lucide-react"
import {
  ComponentPropsWithRef,
  CSSProperties,
  KeyboardEventHandler,
  MouseEvent,
  useMemo,
  useRef,
  ReactNode,
  RefObject,
} from "react"
import { Decimal } from "shared-ts/classes/decimal"

import {
  segmentedFieldClassName,
  trailingSegmentClassName,
} from "./segmentedFieldStyles"
import { classNames } from "../../../../lib/classNames"
import { IconProps } from "../../../Icon"
import { ButtonSize } from "../../control/Button"
import { IconButton } from "../../control/IconButton"
import { TextDecimalInput } from "../text/TextDecimalInput"
import { TextInputVariants } from "../text/TextInput"

const widths = {
  fiveDigits: { group: "w-fit", number: "w-12 shrink-0" },
  sixDigits: { group: "w-fit", number: "w-16 shrink-0" },
  fill: { group: "w-full", number: "min-w-0 flex-1" },
} as const

const groupSizes = {
  small: {
    group: "h-8",
    stepper: ButtonSize.Small,
    stepperWidth: "w-6",
    numberPadding: "px-0.5",
  },
  standard: {
    group: "h-10",
    stepper: ButtonSize.Medium,
    stepperWidth: "w-8",
    numberPadding: "px-1.5",
  },
} as const

const Stepper = ({
  icon,
  size,
  widthClassName,
  disabled,
  onClick,
}: {
  icon: IconProps["icon"]
  size: ButtonSize
  widthClassName: string
  disabled: boolean
  onClick: () => void
}) => (
  <IconButton
    icon={icon}
    size={size}
    disabled={disabled}
    onClick={onClick}
    // aria-hidden because the spinbutton owns the keyboard and value semantics.
    aria-hidden
    tabIndex={-1}
    // The size prop's smallest icon is heavier than the figure beside it.
    iconClassName="size-3.5"
    // h-full, not size-*: a square leaves a strip the hover cannot reach.
    className={classNames(
      "h-full shrink-0 rounded-none focus-visible:ring-0",
      widthClassName
    )}
  />
)

export const QuantityInputVariants = {
  STEPPER: "stepper",
  SEGMENTED: "segmented",
} as const
export type QuantityInputVariant =
  (typeof QuantityInputVariants)[keyof typeof QuantityInputVariants]

type BaseQuantityInputProps = Omit<
  ComponentPropsWithRef<"input">,
  | "className"
  | "defaultValue"
  | "max"
  | "min"
  | "onBlur"
  | "onChange"
  | "style"
  | "value"
  | "width"
> & {
  quantity?: Decimal
  onChangeQuantity: (num: Decimal) => void
  onBlurQuantity?: (num: Decimal) => void
  defaultQuantity?: Decimal
  max?: Decimal
  incrementMax?: Decimal
  min?: Decimal
  decrementMin?: Decimal
  invalid?: boolean
  numDecimalPlaces?: number
  ref?: RefObject<HTMLInputElement | null>
  commitTextOnBlur?: boolean
}

type SegmentedProps = {
  renderNumberSegment?: (numberSegment: ReactNode) => ReactNode
  unitSegment?: ReactNode
  groupSize?: keyof typeof groupSizes
  width: keyof typeof widths
  treatEmptyAsZero?: boolean
}

type StyleOverrides = {
  className?: string
  containerClassName?: string
  style?: CSSProperties
}

type StepperProps = {
  showIconAtMin?: IconProps["icon"]
}

type NeverProps<T> = {
  [K in keyof T]?: never
}

export type QuantityInputProps = BaseQuantityInputProps &
  (
    | ({ variant: typeof QuantityInputVariants.SEGMENTED } & SegmentedProps &
        NeverProps<StepperProps & StyleOverrides>)
    | ({ variant: typeof QuantityInputVariants.STEPPER } & StepperProps &
        StyleOverrides &
        NeverProps<SegmentedProps>)
  )

export const QuantityInput = (props: QuantityInputProps) => {
  const {
    quantity,
    onChangeQuantity,
    className,
    containerClassName,
    defaultQuantity = Decimal.zero(),
    max = Decimal.fromBigSource(999999),
    incrementMax,
    min = Decimal.fromBigSource(-999999),
    decrementMin,
    disabled,
    ref,
    onKeyDown,
    invalid,
    numDecimalPlaces = 0,
    onBlurQuantity,
    variant,
    showIconAtMin,
    commitTextOnBlur = false,
    groupSize = "small",
    unitSegment,
    renderNumberSegment,
    width,
    treatEmptyAsZero,
    ...otherProps
  } = props

  const fallbackQuantity = Decimal.min(max, Decimal.max(min, defaultQuantity))

  const localQuantity = useMemo(() => {
    return quantity ?? fallbackQuantity
  }, [quantity, fallbackQuantity])

  const isEnteringFromClick = useRef(false)
  const selectAllOnClick = {
    onMouseDown: (e: MouseEvent<HTMLInputElement>) => {
      isEnteringFromClick.current = document.activeElement !== e.currentTarget
    },
    onMouseUp: (e: MouseEvent<HTMLInputElement>) => {
      if (isEnteringFromClick.current) {
        e.preventDefault()
        isEnteringFromClick.current = false
      }
    },
  }

  const handleChangeQuantity = (num: Decimal, clamp = true) => {
    if (clamp && (num.gt(max) || num.lt(min))) {
      num = Decimal.min(max, Decimal.max(min, num))
    }

    const committed = Decimal.fromBigSource(num.toFixed(numDecimalPlaces))
    onChangeQuantity(committed)
    return committed
  }

  const onInputBlur = (d: Decimal) => {
    if (commitTextOnBlur) {
      const committed = handleChangeQuantity(d)
      onBlurQuantity?.(committed)
      return
    }
    if (d.lt(min) || d.gt(max)) {
      handleChangeQuantity(d, true)
    }
    onBlurQuantity?.(localQuantity)
  }

  const isIncrementDisabled = disabled || localQuantity.gte(incrementMax ?? max)
  const isDecrementDisabled = disabled || localQuantity.lte(decrementMin ?? min)

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    onKeyDown?.(e)
  }

  if (variant === QuantityInputVariants.SEGMENTED) {
    const step = (by: 1 | -1) =>
      handleChangeQuantity(localQuantity.add(Decimal.fromBigSource(by)))

    return (
      <div
        className={classNames(
          segmentedFieldClassName(!!invalid),
          groupSizes[groupSize].group,
          widths[width].group
        )}
      >
        <Stepper
          icon={Minus}
          size={groupSizes[groupSize].stepper}
          widthClassName={groupSizes[groupSize].stepperWidth}
          disabled={isDecrementDisabled}
          onClick={() => step(-1)}
        />
        <div
          className={classNames(
            // TextInput's wrappers are auto-height, so h-full needs flex here.
            "flex",
            widths[width].number
          )}
        >
          {(renderNumberSegment ?? ((segment) => segment))(
            <TextDecimalInput
              ref={ref}
              variant={TextInputVariants.SUBTLE}
              decimal={localQuantity}
              onChangeDecimal={
                commitTextOnBlur ? undefined : handleChangeQuantity
              }
              onBlurDecimal={onInputBlur}
              numDecimalPlaces={numDecimalPlaces}
              trimTrailingZeros
              treatEmptyAsZero={treatEmptyAsZero}
              disabled={disabled}
              aria-invalid={invalid}
              role="spinbutton"
              aria-valuenow={Number(localQuantity.toString())}
              aria-valuemin={Number(min.toString())}
              aria-valuemax={Number(max.toString())}
              isArrowKeysEnabled
              onDecimalKeyDown={(e) => {
                onInputKeyDown(e)
                if (e.key !== "Escape") e.stopPropagation()
              }}
              onClick={(e) => e.stopPropagation()}
              {...selectAllOnClick}
              containerClassName="flex min-w-0 flex-1"
              className={classNames(
                "text-v2-foreground-default disabled:text-v2-foreground-default-disabled h-full w-full min-w-0 cursor-text rounded-none border-0 tabular-nums",
                "text-center",
                groupSizes[groupSize].numberPadding
              )}
              {...otherProps}
            />
          )}
        </div>
        <Stepper
          icon={Plus}
          size={groupSizes[groupSize].stepper}
          widthClassName={groupSizes[groupSize].stepperWidth}
          disabled={isIncrementDisabled}
          onClick={() => step(1)}
        />
        {unitSegment && (
          <div className={trailingSegmentClassName}>{unitSegment}</div>
        )}
      </div>
    )
  }

  const atMinimum =
    showIconAtMin !== undefined &&
    localQuantity.lte(min.add(Decimal.fromBigSource(1)))

  return (
    <div
      className={classNames(
        "bg-elevation-surface-default rounded-control flex select-none items-stretch overflow-hidden border",
        invalid ? "border-v2-border-danger" : "border-v2-border-default",
        containerClassName
      )}
    >
      <IconButton
        icon={atMinimum ? showIconAtMin : Minus}
        iconClassName={atMinimum ? "text-v2-foreground-danger" : undefined}
        disabled={isDecrementDisabled}
        aria-label={atMinimum ? "Remove" : "Decrease quantity"}
        className="size-8 shrink-0 rounded-none focus-visible:ring-0"
        onClick={() =>
          handleChangeQuantity(localQuantity.sub(Decimal.fromBigSource(1)))
        }
      />
      <TextDecimalInput
        ref={ref}
        aria-label="Quantity"
        variant={TextInputVariants.SUBTLE}
        decimal={localQuantity}
        onChangeDecimal={commitTextOnBlur ? undefined : handleChangeQuantity}
        onBlurDecimal={onInputBlur}
        numDecimalPlaces={numDecimalPlaces}
        trimTrailingZeros
        allowNegative={false}
        disabled={disabled}
        aria-invalid={invalid}
        isArrowKeysEnabled
        onDecimalKeyDown={(e) => {
          onInputKeyDown(e)
          e.stopPropagation()
        }}
        onClick={(e) => e.stopPropagation()}
        containerClassName="flex-1"
        className={classNames(
          "color-default border-v2-border-default text-field h-8 min-w-10 rounded-none border-x border-y-0 border-solid text-center tabular-nums",
          className
        )}
        {...otherProps}
      />
      <IconButton
        icon={Plus}
        disabled={isIncrementDisabled}
        aria-label="Increase quantity"
        className="size-8 shrink-0 rounded-none focus-visible:ring-0"
        onClick={() =>
          handleChangeQuantity(localQuantity.add(Decimal.fromBigSource(1)))
        }
      />
    </div>
  )
}
